import React, { useState } from 'react';
import { LandingPage } from './components/LandingPage';
import { Login } from './components/Login';
import { Navigation, AppView } from './components/Navigation';
import { QuoteBuilder } from './components/QuoteBuilder';
import { AIGenerator } from './components/AIGenerator';
import { SummaryPanel } from './components/SummaryPanel';
import { ComparisonDashboard } from './components/ComparisonDashboard';
import { CommandCenter } from './components/CommandCenter';
import { QuoteOutput } from './components/QuoteOutput';
import { MyQuotes } from './components/MyQuotes';
import { saveQuote } from './services/quoteService';
import { useUser } from './contexts/UserContext';
import {
  QuoteState, AIQuoteResponse, SolutionType, MultiCarrierResult
} from './types';
import { Loader2, CheckCircle, AlertCircle, Menu, X } from 'lucide-react';
import { Routes, Route, Link } from 'react-router-dom';
import AdminBillReview from './components/AdminBillReview';
import { AdminRoute } from './components/AdminRoute';

const DEFAULT_SOLUTION: MultiCarrierResult = {
  carriers: [],
  combined: {
    monthlyServiceTotal: 0, monthlyHardwareTotal: 0,
    taxesAndFees: 0, etfTotal: 0, totalDue: 0,
    deviceCounts: { phone: 0, tablet: 0, watch: 0, connected: 0, other: 0 },
  }
};

const DEFAULT_QUOTE: QuoteState = {
  customerName: '',
  companyName:  '',
  term:         24,
  sellerInfo:   { orgName: '', name: '', title: '', email: '', phone: '' },
  items:        [],
  promos:       [],
};

type AppViewTop = 'landing' | 'login' | 'app';

type ToastType = { msg: string; type: 'success' | 'error' };

function uid() { return Math.random().toString(36).slice(2, 9); }

function attachIds(r: AIQuoteResponse): Partial<QuoteState> {
  return {
    items:    r.items.map(it => ({
      ...it,
      id:      uid(),
      devices: it.devices.map(d => ({ ...d, id: uid() })),
    })),
    promos:   r.recommendedPromos.map(p => ({ ...p, id: uid() })),
    insights: r.insights,
    analysis: r.analysis,
  };
}

export default function App() {
  const { user, loadingAuth }                      = useUser();
  const [topView, setTopView]                      = useState<AppViewTop>('landing');
  const [mainView, setMainView]                    = useState<AppView>('builder');
  const [quote, setQuote]                          = useState<QuoteState>(DEFAULT_QUOTE);
  const [currentSolution, setCurrentSolution]      = useState<MultiCarrierResult>(DEFAULT_SOLUTION);
  const [showOutput, setShowOutput]                = useState(false);
  const [saving, setSaving]                        = useState(false);
  const [toast, setToast]                          = useState<ToastType | null>(null);
  const [sidebarOpen, setSidebarOpen]              = useState(false);

  // Auto-navigate to app when logged in
  React.useEffect(() => {
    if (!loadingAuth && user) setTopView('app');
    if (!loadingAuth && !user && topView === 'app') setTopView('landing');
  }, [user, loadingAuth]);

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleAIUpdate = (r: AIQuoteResponse) => {
    setQuote(q => ({ ...q, ...attachIds(r) }));
    showToast('AI quote generated successfully!');
  };

  const handleBillAnalyzed = (cs: MultiCarrierResult) => {
    setCurrentSolution(cs);
    const providers = cs.carriers.map(c => c.providerName).join(', ');
    showToast(`Bills from ${providers || 'carriers'} analyzed — data applied to Comparison.`);
    setMainView('dashboard');
  };

  const handleSave = async () => {
    if (!user || !quote.customerName) {
      showToast('Please add a customer name before saving.', 'error');
      return;
    }
    setSaving(true);
    try {
      await saveQuote(user.uid, quote);
      showToast('Quote saved successfully!');
    } catch {
      showToast('Failed to save. Check your connection.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadQuote = (q: QuoteState) => {
    setQuote(q);
    setMainView('builder');
    showToast('Quote loaded into builder.');
  };

  // Loading screen
  if (loadingAuth) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#0D0D1A] flex items-center justify-center animate-pulse overflow-hidden shadow-xl shadow-purple-500/30">
            <img src="/nxtkonekt-logo.svg" alt="NxtKonekt" className="w-full h-full" />
          </div>
          <Loader2 size={24} className="text-purple-500 animate-spin" />
          <p className="text-gray-600 text-sm">Loading NxtKonekt ProQuote...</p>
        </div>
      </div>
    );
  }

  // Main app
  return (
    <Routes>
      <Route path="/admin/bill-review" element={<AdminRoute><AdminBillReview /></AdminRoute>} />
      <Route path="/login" element={<Login onBack={() => setTopView('landing')} />} />
      <Route path="*" element={
        <>
          {topView === 'landing' && (
            <LandingPage onGetStarted={() => setTopView('login')} />
          )}

          {topView === 'login' && (
            <Login onBack={() => setTopView('landing')} />
          )}

          {topView === 'app' && (
            <div className="flex h-screen bg-[#0A0A0F] overflow-hidden">
              {/* Mobile sidebar backdrop */}
              {sidebarOpen && (
                <div
                  className="fixed inset-0 z-40 bg-black/60 lg:hidden"
                  onClick={() => setSidebarOpen(false)}
                />
              )}

              {/* Sidebar */}
              <div className={`
                fixed lg:static inset-y-0 left-0 z-50 lg:z-auto
                transition-transform duration-300
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
              `}>
                <Navigation
                  currentView={mainView}
                  onNavigate={v => { setMainView(v); setSidebarOpen(false); }}
                  user={user}
                />
              </div>

              {/* Main */}
              <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                {/* Top bar */}
                <header className="h-14 flex items-center px-4 lg:px-6 border-b border-white/5 bg-[#0A0A0F]/80 backdrop-blur-sm shrink-0 gap-3">
                  <button
                    onClick={() => setSidebarOpen(true)}
                    className="lg:hidden p-2 rounded-lg text-gray-500 hover:text-white hover:bg-white/5 transition-all"
                  >
                    <Menu size={18} />
                  </button>
                  <h1 className="font-bold text-white text-base">
                    {mainView === 'builder'       && 'Quote Builder'}
                    {mainView === 'dashboard'     && 'Comparison Dashboard'}
                    {mainView === 'commandCenter' && 'Command Center'}
                    {mainView === 'history'       && 'My Quotes'}
                  </h1>
                  {quote.customerName && mainView !== 'history' && (
                    <span className="text-xs text-gray-600 ml-auto hidden sm:block">
                      {quote.customerName}{quote.companyName ? ` · ${quote.companyName}` : ''}
                    </span>
                  )}
                </header>

                {/* Page content */}
                <main className="flex-1 overflow-y-auto p-4 lg:p-6">
                  {mainView === 'builder' && (
                    <div className="max-w-6xl mx-auto">
                      <div className="grid xl:grid-cols-[1fr_320px] gap-6">
                        {/* Left: builder + AI */}
                        <div className="space-y-5 min-w-0">
                          <AIGenerator
                            companyName={quote.companyName || 'this organization'}
                            onQuoteGenerated={handleAIUpdate}
                            onBillAnalyzed={handleBillAnalyzed}
                          />
                          <QuoteBuilder quote={quote} onChange={setQuote} />
                        </div>

                        {/* Right: summary */}
                        <div className="xl:sticky xl:top-0 xl:h-fit">
                          <SummaryPanel
                            quote={quote}
                            currentSolution={currentSolution.carriers.length > 0 ? currentSolution : undefined}
                            onSave={handleSave}
                            onPrint={() => setShowOutput(true)}
                            saving={saving}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {mainView === 'dashboard' && (
                    <div className="max-w-6xl mx-auto">
                      <ComparisonDashboard
                        quote={quote}
                        currentSolution={currentSolution}
                        onUpdateCurrentSolution={setCurrentSolution}
                      />
                    </div>
                  )}

                  {mainView === 'commandCenter' && (
                    <div className="max-w-5xl mx-auto">
                      <CommandCenter
                        quote={quote}
                        currentSolution={currentSolution}
                        insights={quote.insights}
                      />
                    </div>
                  )}

                  {mainView === 'history' && (
                    <div className="max-w-5xl mx-auto">
                      <MyQuotes onLoad={handleLoadQuote} />
                    </div>
                  )}
                </main>
              </div>

              {/* Quote output modal */}
              {showOutput && (
                <QuoteOutput quote={quote} onClose={() => setShowOutput(false)} />
              )}
            </div>
          )}

          {/* Toast */}
          {toast && (
            <div className={`
              fixed bottom-5 right-5 z-[100] flex items-center gap-3 px-4 py-3 rounded-2xl shadow-2xl
              border text-sm font-semibold animate-slide-up
              ${toast.type === 'success'
                ? 'bg-emerald-950 border-emerald-500/30 text-emerald-300'
                : 'bg-red-950 border-red-500/30 text-red-300'
              }
            `}>
              {toast.type === 'success'
                ? <CheckCircle size={16} className="text-emerald-400" />
                : <AlertCircle size={16} className="text-red-400" />
              }
              {toast.msg}
            </div>
          )}

          {/* Subtle Admin Link */}
          <div style={{ 
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            textAlign: 'center', 
            padding: '12px', 
            zIndex: 40,
            pointerEvents: 'none'
          }}>
            <Link 
              to="/admin/bill-review"
              style={{ 
                fontSize: '0.65rem', 
                color: 'rgba(255,255,255,0.1)', 
                textDecoration: 'none',
                letterSpacing: '1.5px',
                pointerEvents: 'auto',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.1)'}
            >
              ADMIN
            </Link>
          </div>
        </>
      } />
    </Routes>
  );
}
